import {
  AsyncAutocompleteWithAdd,
  AsyncCallArgs,
  CardSpg,
  CircularLoader,
  FormSection,
  GridContainer,
  SimpleConfirmDialog,
  useAsyncCall,
} from "@duosoftbg/nacid-components";
import { CardContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../../../../../hooks/redux/base/useAppDispatch";
import React, { useEffect, useState } from "react";
import {
  addUserToGroup,
  addUserToRole,
  getFullUserDetails,
  getGroupsWithAdditionalAutocomplete,
  getRolesWithAdditionalParamsAutocomplete,
  removeUserFromGroup,
  removeUserFromRole,
} from "../../../../../axios/api/services";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import UserMainDetailsForm from "../dialog/parts/UserMainDetailsForm";
import ScrollableGridContainer from "../../../common/grid/ScrollableGridContainer";
import RolesDialogList from "../../groupsAndRoles/dialog/parts/RolesDialogList";
import GroupsDialogList from "../../groupsAndRoles/dialog/parts/GroupsDialogList";

const UserEdit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { asyncCall } = useAsyncCall();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [removeGroup, setRemoveGroup] = useState(null);
  const [removeRole, setRemoveRole] = useState(null);
  const [confirmModalOpenRole, setConfirmModalOpenRole] = useState(false);
  const [confirmModalOpenGroup, setConfirmModalOpenGroup] = useState(false);

  useEffect(() => {
    if (id) {
      const getUserCallArgs: AsyncCallArgs = {
        promise: getFullUserDetails(id),
        onSuccess: (response) => {
          setUser(response);
          setRoles(response.roles);
          setGroups(response.groups);
        },
        onError: () => {
          setUser(null);
        },
      };
      asyncCall(getUserCallArgs);
    }
  }, [id, asyncCall, dispatch, t]);

  const handleAddRoleToUser = (role) => {
    if (!roles.find((arrayRole) => arrayRole.id === role.id)) {
      const addRoleCallArgs: AsyncCallArgs = {
        promise: addUserToRole(role.id, user.user.id),
        onSuccess: (response) => {
          if (response) {
            const newRoles = [...roles, role];
            setRoles(newRoles);
            toast.success(t("m.user.role.add.success"));
          }
        },
      };
      asyncCall(addRoleCallArgs);
    }
  };

  const handleAddGroupToUser = (group) => {
    if (!groups.find((arrayGroup) => arrayGroup.id === group.id)) {
      const addGroupCallArgs: AsyncCallArgs = {
        promise: addUserToGroup(group.id, user.user.id),
        onSuccess: (response) => {
          if (response) {
            const newGroups = [...groups, group];
            setGroups(newGroups);
            toast.success(t("m.user.group.add.success"));
          }
        },
      };
      asyncCall(addGroupCallArgs);
    }
  };

  const handleRemoveRoleClick = (role) => {
    setRemoveRole(role);
    setConfirmModalOpenRole(true);
  };

  const handleRemoveGroupClick = (group) => {
    setRemoveGroup(group);
    setConfirmModalOpenGroup(true);
  };

  const handleRemoveRoleFromUser = () => {
    const removeRoleCallArgs: AsyncCallArgs = {
      promise: removeUserFromRole(removeRole.id, user.user.id),
      onSuccess: (response) => {
        if (response) {
          const newRoles = roles.filter((listRole) => removeRole.id !== listRole.id);
          setRoles(newRoles);
          setRemoveRole(null);
          setConfirmModalOpenRole(false);
          toast.success(t("m.user.role.remove.success"));
        }
      },
    };
    asyncCall(removeRoleCallArgs);
  };

  const handleRemoveGroupFromUser = () => {
    const removeGroupCallArgs: AsyncCallArgs = {
      promise: removeUserFromGroup(removeGroup.id, user.user.id),
      onSuccess: (response) => {
        if (response) {
          const newGroups = groups.filter((listGroup) => removeGroup.id !== listGroup.id);
          setGroups(newGroups);
          setRemoveGroup(null);
          setConfirmModalOpenGroup(false);
          toast.success(t("m.user.group.remove.success"));
        }
      },
    };
    asyncCall(removeGroupCallArgs);
  };

  return (
    <CardSpg my={4} style={{ overflow: "visible" }}>
      <CardContent style={{ padding: 24, position: "relative" }}>
        <SimpleConfirmDialog
          open={confirmModalOpenRole}
          setOpen={setConfirmModalOpenRole}
          dialogTitleText={"m.confirm"}
          alertText={"m.user.role.remove.confirm"}
          alertType={"warning"}
          onConfirm={handleRemoveRoleFromUser}
        />
        <SimpleConfirmDialog
          open={confirmModalOpenGroup}
          setOpen={setConfirmModalOpenGroup}
          dialogTitleText={"m.confirm"}
          alertText={"m.user.group.remove.confirm"}
          alertType={"warning"}
          onConfirm={handleRemoveGroupFromUser}
        />
        {!user && <CircularLoader />}
        {user && (
          <>
            <UserMainDetailsForm user={user.user} />

            <FormSection label={"t.user.roles"}>
              <GridContainer spacing={3} mt={0}>
                <AsyncAutocompleteWithAdd
                  key={`roles-autocomplete-key-${roles.length}`}
                  onAddRecord={handleAddRoleToUser}
                  setOptionText={(option) => option.name}
                  getOptionLabel={(option) => option.id}
                  setInputOnSelect={(option) => option.name}
                  autocompleteFn={getRolesWithAdditionalParamsAutocomplete}
                  additionalParams={{ excludedUserId: user.user.id }}
                  label={"l.select.role"}
                />
              </GridContainer>

              <ScrollableGridContainer>
                <RolesDialogList roles={roles} isEdit={true} onRemove={handleRemoveRoleClick} />
              </ScrollableGridContainer>
            </FormSection>

            <FormSection label={"t.user.groups"}>
              <GridContainer spacing={3} mt={0}>
                <AsyncAutocompleteWithAdd
                  key={`groups-autocomplete-key-${groups.length}`}
                  onAddRecord={handleAddGroupToUser}
                  setOptionText={(option) => option.name}
                  getOptionLabel={(option) => option.id}
                  setInputOnSelect={(option) => option.name}
                  autocompleteFn={getGroupsWithAdditionalAutocomplete}
                  additionalParams={{ excludedUserId: user.user.id }}
                  label={"l.select.group"}
                />
              </GridContainer>

              <ScrollableGridContainer>
                <GroupsDialogList groups={groups} isEdit={true} onRemove={handleRemoveGroupClick} />
              </ScrollableGridContainer>
            </FormSection>
          </>
        )}
      </CardContent>
    </CardSpg>
  );
};
export default UserEdit;
